import { Col, Form, Input, InputNumber, Modal, Row } from "antd";
import { useIntl } from "react-intl";
import { SelectStage } from "~components/SelectStage/SelectStage.tsx";
import { useCreateSeasonPool } from "~pages/SeasonPools/hooks/useCreateSeasonPool.ts";
import generateUUIdv4 from "~utilities/generateUUIdv4.js";

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};

export const AddSeasonPoolModal = ({ isOpen, setIsOpen }: Props) => {
  const { formatMessage } = useIntl();
  const [form] = Form.useForm();
  const { mutateAsync: createSeasonPool, isPending } = useCreateSeasonPool();

  const handleClose = () => {
    form.resetFields();
    setIsOpen(false);
  };

  const handleSubmit = async () => {
    const formValues = await form.validateFields();

    await createSeasonPool({
      poolCode: generateUUIdv4(),
      nameLocal: formValues.nameLocal,
      nameLatin: formValues.nameLatin || null,
      abbreviationLocal: formValues.abbreviationLocal || null,
      abbreviationLatin: formValues.abbreviationLatin || null,
      externalId: formValues.externalId || null,
      poolOrder: formValues.poolOrder ?? null,
      stageCode: formValues.stageCode?.value ? formValues.stageCode.value : null,
    });

    handleClose();
  };

  return (
    <Modal
      width={550}
      open={isOpen}
      closable={true}
      destroyOnHidden
      onCancel={handleClose}
      title={formatMessage({ id: "pool.add" })}
      okText={formatMessage({ id: "add" })}
      onOk={() => void handleSubmit()}
      okButtonProps={{ loading: isPending }}
      cancelText={formatMessage({ id: "cancel" })}
    >
      <Form form={form} labelCol={{ span: 11 }} wrapperCol={{ span: 10 }} colon={false} labelWrap={true}>
        <Row>
          <Col span={13}>
            <Form.Item
              name="nameLocal"
              label={formatMessage({ id: "name.local" })}
              tooltip={formatMessage({ id: "form.help.local.name.item" }, { item: formatMessage({ id: "pool" }) })}
              rules={[
                {
                  required: true,
                  message: formatMessage({ id: "form.help.required.field" }, { field: formatMessage({ id: "name.local" }) }),
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item
              labelCol={{ span: 12 }}
              name="abbreviationLocal"
              label={formatMessage({ id: "name.local.abbreviation" })}
              tooltip={formatMessage({ id: "form.help.local.abbreviation" })}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={13}>
            <Form.Item
              name="nameLatin"
              label={formatMessage({ id: "name.latin" })}
              tooltip={formatMessage({ id: "form.help.latin.name.item" }, { item: formatMessage({ id: "pool" }) })}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item
              labelCol={{ span: 12 }}
              name="abbreviationLatin"
              label={formatMessage({ id: "name.latin.abbreviation" })}
              tooltip={formatMessage({ id: "form.help.latin.abbreviation" })}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={13}>
            <Form.Item name="externalId" label={formatMessage({ id: "externalId" })} tooltip={formatMessage({ id: "externalId" })}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item
              labelCol={{ span: 14 }}
              rules={[
                {
                  required: true,
                  message: formatMessage({ id: "form.help.required.field" }, { field: formatMessage({ id: "pool.order" }) }),
                },
              ]}
              name="poolOrder"
              label={formatMessage({ id: "pool.order" })}
              tooltip={formatMessage(
                { id: "form.help.order.different.within.itemA.itemB" },
                {
                  itemA: formatMessage({ id: "pools" }),
                  itemB: formatMessage({ id: "season" }),
                },
              )}
            >
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={13}>
            <Form.Item name="stageCode" label={formatMessage({ id: "stage" })} tooltip={formatMessage({ id: "form.help.pool.stage" })}>
              <SelectStage />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
